import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import TaskSubtasks from './TaskSubtasks';
import TaskConversations from './TaskConversation';
import './TasksPage.css';
import HomeButton from './HomeButton';

const CHAT_API_BASE = '/api/chat';

const authFetch = async (url, opts = {}) => {
  const token = await api.ensureAuthenticated();
  return fetch(url, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` } });
};

// Status catalogue. Colors are deliberately restrained — one small dot per
// group so users read status by shape+text, not by a wall of color.
// Anything the backend hasn't seen falls through to `unknown`.
const STATUS_META = {
  'Not Started':   { key: 'Not Started',   label: 'Not Started',  color: '#c2b280', tone: 'todo' },
  'In Progress':   { key: 'In Progress',   label: 'In Progress',  color: '#5aa9e6', tone: 'progress' },
  'Pending Input': { key: 'Pending Input', label: 'Pending Input',color: '#e0a458', tone: 'pending' },
  'Deferred':      { key: 'Deferred',      label: 'Deferred',     color: '#8a8266', tone: 'deferred' },
  'Blocked':       { key: 'Blocked',       label: 'Blocked',      color: '#e57373', tone: 'blocked' },
  'Completed':     { key: 'Completed',     label: 'Completed',    color: '#6bcf70', tone: 'done' },
};
const statusMeta = (raw) => STATUS_META[raw] || { key: raw || 'Unknown', label: raw || 'Unknown', color: '#8a8266', tone: 'unknown' };

const PRIORITY_META = {
  Urgent: { label: 'Urgent', color: '#e57373', icon: '⚠' },
  High:   { label: 'High',   color: '#e0a458', icon: '↑' },
  Medium: { label: 'Medium', color: '#c2b280', icon: '—' },
  Low:    { label: 'Low',    color: '#8a8266', icon: '↓' },
};
const priorityMeta = (raw) => PRIORITY_META[raw] || { label: raw || '—', color: '#8a8266', icon: '·' };

const GROUP_OPTIONS = [
  { key: 'status',     label: 'Status' },
  { key: 'assignee',   label: 'Assignee' },
  { key: 'project',    label: 'Project' },
  { key: 'due',        label: 'Due date' },
  { key: 'priority',   label: 'Priority' },
];

const SORT_OPTIONS = [
  { key: 'date_due',      label: 'Due date' },
  { key: 'priority',      label: 'Priority' },
  { key: 'name',          label: 'Name' },
  { key: 'date_entered',  label: 'Created' },
  { key: 'status',        label: 'Status' },
];

const PRIORITY_ORDER = { Urgent: 0, High: 1, Medium: 2, Low: 3 };
const STATUS_ORDER = { 'Not Started': 0, 'In Progress': 1, 'Pending Input': 2, 'Blocked': 3, 'Deferred': 4, 'Completed': 5 };

const initialsOf = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};

const fmtDueDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '—';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const t = new Date(d);
  t.setHours(0, 0, 0, 0);
  const diff = Math.round((t - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const isOverdue = (iso, status) => {
  if (!iso || status === 'Completed') return false;
  const d = new Date(iso.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const t = new Date(d); t.setHours(0, 0, 0, 0);
  return t < today;
};

const TasksPage = ({ onBack, currentUser }) => {
  const [tasks, setTasks] = useState([]);
  const [projectsByTaskId, setProjectsByTaskId] = useState({}); // { taskId: {id, name} }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [scope, setScope] = useState('my'); // 'my' | 'all'
  const [search, setSearch] = useState('');
  const [groupBy, setGroupBy] = useState('status');
  const [sortBy, setSortBy] = useState('date_due');
  const [showCompleted, setShowCompleted] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  // Subtasks aren't in the flat tasks list (project-tasks.php?scope=all
  // excludes them), so when TaskDetailView opens a subtask we cache it
  // here by id so `selectedTask` can still resolve it.
  const [openedTasks, setOpenedTasks] = useState({});
  const openTask = (id, taskObj) => {
    if (taskObj) {
      setOpenedTasks(prev => ({ ...prev, [id]: { id, ...(taskObj.attributes || {}) } }));
    }
    setSelectedId(id);
  };
  const [subtaskCounts, setSubtaskCounts] = useState({}); // { taskId: { total, done } }
  // Bumped after any task patch — forces useSubtaskCounts to re-fetch
  // even though the parent id list hasn't changed.
  const [countsVersion, setCountsVersion] = useState(0);

  // ---- data ----
  const fetchTasks = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ scope: 'all', page: '1', pageSize: '200' });
      if (currentUser?.id) params.set('user_id', currentUser.id);
      const res = await authFetch(`${CHAT_API_BASE}/project-tasks.php?${params.toString()}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load tasks');
      const rows = (data.data || []).map(d => ({ id: d.id, ...(d.attributes || {}) }));
      setTasks(rows);

      // Fetch project associations in the same shape ModulePage uses.
      try {
        const pr = await fetch(`${CHAT_API_BASE}/record-project.php?module=Tasks`);
        const pd = await pr.json();
        if (pd.success && pd.data) {
          const map = {};
          Object.entries(pd.data).forEach(([taskId, info]) => {
            if (info?.project_id) map[taskId] = { id: info.project_id, name: info.project_name || 'Project' };
          });
          setProjectsByTaskId(map);
        }
      } catch { /* non-fatal */ }
    } catch (e) {
      setError(e.message || 'Failed to load tasks');
    }
    setLoading(false);
  }, [currentUser]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Fetch subtask progress for visible tasks.
  const fetchedCounts = useSubtaskCounts(tasks, countsVersion);
  useEffect(() => {
    setSubtaskCounts(prev => ({ ...prev, ...fetchedCounts }));
  }, [fetchedCounts]);

  // ---- filter + sort ----
  const visibleTasks = useMemo(() => {
    let list = tasks.slice();
    if (scope === 'my' && currentUser?.id) {
      list = list.filter(t => t.assigned_user_id === currentUser.id);
    }
    if (!showCompleted) {
      list = list.filter(t => t.status !== 'Completed');
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(t => (t.name || '').toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      if (sortBy === 'priority') return (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9);
      if (sortBy === 'status')   return (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
      if (sortBy === 'name')     return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'date_due') return String(a.date_due || '').localeCompare(String(b.date_due || ''));
      return String(b.date_entered || '').localeCompare(String(a.date_entered || ''));
    });
    return list;
  }, [tasks, scope, showCompleted, search, sortBy, currentUser]);

  // ---- group ----
  const groups = useMemo(
    () => groupTasksBy(visibleTasks, groupBy, projectsByTaskId),
    [visibleTasks, groupBy, projectsByTaskId]
  );

  // ---- selection ----
  const selectedTask = tasks.find(t => t.id === selectedId) || openedTasks[selectedId] || null;

  // Update a task field via project-tasks.php PUT.
  const patchTask = async (id, changes) => {
    // Optimistic update. Subtasks live in the openedTasks cache instead
    // of `tasks`, so we mirror the change into whichever slot has the id
    // — otherwise the right-column selects snap back to the old value.
    setTasks(prev => prev.map(t => t.id === id ? { ...t, ...changes } : t));
    setOpenedTasks(prev => prev[id] ? { ...prev, [id]: { ...prev[id], ...changes } } : prev);
    try {
      // project-tasks.php PUT reads `id` from the JSON body (not the URL);
      // passing it only in the query string returned "Task ID is required"
      // and no change was ever persisted for subtasks.
      await authFetch(`${CHAT_API_BASE}/project-tasks.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...changes, user_id: currentUser?.id }),
      });
    } catch { /* keep optimistic value */ }
    // A completed subtask flips the parent's Subtasks progress bar;
    // status changes on top-level tasks are cheap to re-scan too.
    setCountsVersion(v => v + 1);
  };

  const toggleComplete = (t) => {
    const next = t.status === 'Completed' ? 'Not Started' : 'Completed';
    patchTask(t.id, { status: next });
  };

  // Create a new empty task via POST project-tasks.php.
  const createTask = async (nameFromRow) => {
    const name = (nameFromRow || window.prompt('New task name:', 'New task') || '').trim();
    if (!name) return;
    try {
      const res = await authFetch(`${CHAT_API_BASE}/project-tasks.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, status: 'Not Started', user_id: currentUser?.id }),
      });
      const data = await res.json();
      if (data.success) await fetchTasks();
    } catch { /* ignore */ }
  };

  // ------------------------ render ------------------------

  if (selectedTask) {
    return (
      <TaskDetailView
        task={selectedTask}
        allTasks={visibleTasks}
        projectsByTaskId={projectsByTaskId}
        onBack={() => setSelectedId(null)}
        onSelect={openTask}
        onPatch={patchTask}
        onRefresh={fetchTasks}
        currentUser={currentUser}
        onBackToDashboard={onBack}
      />
    );
  }

  return (
    <div className="tp-page">
      <div className="tp-header">
        <div className="tp-header-left">
          <HomeButton onClick={onBack} />
          <button className="back-button" onClick={onBack}>← Back</button>
          <div>
            <h1 className="tp-title">Tasks</h1>
            <p className="tp-subtitle">Plan, assign and track work across every project.</p>
          </div>
        </div>
        <button className="tp-new-btn" onClick={() => createTask()}>+ New Task</button>
      </div>

      <nav className="tp-tabs" role="tablist" aria-label="Task view">
        <button role="tab" aria-selected={scope === 'my'} className={`tp-tab ${scope === 'my' ? 'active' : ''}`} onClick={() => setScope('my')}>My Tasks</button>
        <button role="tab" aria-selected={scope === 'all'} className={`tp-tab ${scope === 'all' ? 'active' : ''}`} onClick={() => setScope('all')}>All Tasks</button>
        <button role="tab" className="tp-tab" disabled title="Coming soon">Board</button>
        <button role="tab" className="tp-tab" disabled title="Coming soon">Calendar</button>
        <span className="tp-tab-spacer" />
      </nav>

      <div className="tp-toolbar">
        <div className="tp-toolbar-left">
          <div className="tp-search">
            <span className="tp-search-icon" aria-hidden="true">🔍</span>
            <input
              type="search"
              placeholder="Search tasks…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              aria-label="Search tasks"
            />
          </div>
          <label className="tp-select-wrap">
            <span className="tp-select-label">Group:</span>
            <select value={groupBy} onChange={e => setGroupBy(e.target.value)} aria-label="Group by">
              {GROUP_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </label>
          <label className="tp-select-wrap">
            <span className="tp-select-label">Sort:</span>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} aria-label="Sort by">
              {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </label>
        </div>
        <label className="tp-toggle">
          <span>Show completed</span>
          <input type="checkbox" checked={showCompleted} onChange={e => setShowCompleted(e.target.checked)} />
          <span className="tp-toggle-track" aria-hidden="true"><span className="tp-toggle-thumb" /></span>
        </label>
      </div>

      <div className="tp-columns" role="row">
        <div aria-hidden="true" />
        <div aria-hidden="true" />
        <div>Task</div>
        <div>Project</div>
        <div>Assignee</div>
        <div>Priority</div>
        <div>Due date</div>
        <div>Subtasks</div>
        <div aria-hidden="true">💬</div>
      </div>

      {loading && <div className="tp-state">Loading tasks…</div>}
      {error && <div className="tp-state tp-state-err">{error}</div>}
      {!loading && !error && groups.length === 0 && (
        <div className="tp-state">
          <div className="tp-empty-icon" aria-hidden="true">✓</div>
          <div className="tp-empty-title">No tasks match your filters</div>
          <div className="tp-empty-body">Try a different group, or turn on "Show completed".</div>
        </div>
      )}

      {groups.map(g => (
        <TaskGroup
          key={g.name}
          group={g}
          projectsByTaskId={projectsByTaskId}
          subtaskCounts={subtaskCounts}
          onOpen={setSelectedId}
          onToggleComplete={toggleComplete}
          onAddTaskInGroup={() => createTask()}
        />
      ))}
    </div>
  );
};

// ------------------------ TaskGroup ------------------------

/**
 * Small helper: group a flat list of tasks by the given field
 * ('status' | 'assignee' | 'priority' | 'due') and return the same
 * { name, color, items } shape TaskGroup renders. Exported so other
 * pages (Project → Tasks) can reuse the row look without duplicating
 * the grouping logic.
 */
export const groupTasksBy = (tasks, groupBy, projectsByTaskId = {}) => {
  const bucketKey = (t) => {
    switch (groupBy) {
      case 'assignee': return t.assigned_user_name || 'Unassigned';
      case 'project':  return projectsByTaskId[t.id]?.name || 'No project';
      case 'due': {
        if (!t.date_due) return 'No due date';
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const d = new Date((t.date_due).replace(' ', 'T')); d.setHours(0, 0, 0, 0);
        const diff = Math.round((d - today) / 86400000);
        if (diff < 0) return 'Overdue';
        if (diff === 0) return 'Today';
        if (diff === 1) return 'Tomorrow';
        if (diff <= 7) return 'This week';
        if (diff <= 30) return 'This month';
        return 'Later';
      }
      case 'priority': return t.priority || 'No priority';
      case 'status':
      default: return statusMeta(t.status).label;
    }
  };
  const bucketColor = (name, sampleTask) => {
    if (groupBy === 'status') return statusMeta(sampleTask?.status).color;
    if (groupBy === 'priority') return priorityMeta(sampleTask?.priority).color;
    if (groupBy === 'due') {
      return name === 'Overdue' ? '#e57373'
        : name === 'Today' ? '#5aa9e6'
        : name === 'Tomorrow' ? '#c2b280'
        : name === 'No due date' ? '#8a8266'
        : '#a89968';
    }
    return '#c2b280';
  };
  const map = new Map();
  for (const t of tasks) {
    const k = bucketKey(t);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(t);
  }
  const arr = Array.from(map.entries()).map(([name, items]) => ({
    name, items, color: bucketColor(name, items[0]),
  }));
  if (groupBy === 'status') {
    arr.sort((a, b) => {
      const raw = (g) => Object.entries(STATUS_META).find(([, v]) => v.label === g.name)?.[0] || g.name;
      return (STATUS_ORDER[raw(a)] ?? 99) - (STATUS_ORDER[raw(b)] ?? 99);
    });
  } else if (groupBy === 'priority') {
    arr.sort((a, b) => (PRIORITY_ORDER[a.name] ?? 99) - (PRIORITY_ORDER[b.name] ?? 99));
  } else if (groupBy === 'due') {
    const ord = { 'Overdue': 0, 'Today': 1, 'Tomorrow': 2, 'This week': 3, 'This month': 4, 'Later': 5, 'No due date': 6 };
    arr.sort((a, b) => (ord[a.name] ?? 99) - (ord[b.name] ?? 99));
  } else {
    arr.sort((a, b) => a.name.localeCompare(b.name));
  }
  return arr;
};

/**
 * Column header strip — matches the row's grid so labels line up
 * with row cells. Exported so embedded task lists (Project → Tasks)
 * can render the same header.
 */
export const TaskListColumns = () => (
  <div className="tp-columns" role="row">
    <div aria-hidden="true" />
    <div aria-hidden="true" />
    <div>Task</div>
    <div>Project</div>
    <div>Assignee</div>
    <div>Priority</div>
    <div>Due date</div>
    <div>Subtasks</div>
    <div aria-hidden="true">💬</div>
  </div>
);

const TaskGroup = ({ group, projectsByTaskId, subtaskCounts, onOpen, onToggleComplete, onAddTaskInGroup }) => {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <section className="tp-group" aria-label={`${group.name} (${group.items.length})`}>
      <header className="tp-group-header">
        <button className="tp-group-caret" aria-label={collapsed ? 'Expand group' : 'Collapse group'} onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? '▸' : '▾'}
        </button>
        <span className="tp-group-dot" style={{ background: group.color }} aria-hidden="true" />
        <span className="tp-group-name">{group.name}</span>
        <span className="tp-group-count">{group.items.length}</span>
      </header>
      {!collapsed && (
        <>
          {group.items.map(t => (
            <TaskRow
              key={t.id}
              task={t}
              project={projectsByTaskId[t.id]}
              subtaskProgress={subtaskCounts[t.id]}
              onOpen={onOpen}
              onToggleComplete={onToggleComplete}
            />
          ))}
          <button className="tp-add-in-group" onClick={onAddTaskInGroup}>+ Add task</button>
        </>
      )}
    </section>
  );
};

// ------------------------ TaskRow ------------------------

export { TaskGroup };

/**
 * Fetch subtask progress ({total, done}) for a list of parent tasks.
 * Returns { [taskId]: { total, done } } for every task that has at least
 * one subtask. Refetches whenever the set of task ids changes.
 * Exported so other pages that reuse TaskGroup can populate the
 * Subtasks column too. Caps at the first 40 tasks per pass to keep
 * the call count sane.
 */
export function useSubtaskCounts(tasks, refreshKey = 0) {
  const [counts, setCounts] = useState({});
  // Depend on the JOINED id list so React only re-runs when the id set
  // actually changes (not on every parent re-render). `refreshKey` is
  // an escape hatch — bump it after a task patch to force a re-fetch
  // when only a subtask's status changed (the id list stays the same,
  // so a naive dep wouldn't refetch).
  const idKey = (tasks || []).slice(0, 40).map(t => t.id).join(',');
  useEffect(() => {
    if (!idKey) { setCounts({}); return undefined; }
    let cancelled = false;
    (async () => {
      const ids = idKey.split(',');
      const map = {};
      await Promise.all(ids.map(async id => {
        try {
          const r = await authFetch(`${CHAT_API_BASE}/project-tasks.php?parent_task_id=${encodeURIComponent(id)}`);
          const d = await r.json();
          if (d.success && Array.isArray(d.data)) {
            const total = d.data.length;
            const done = d.data.filter(x => (x.attributes?.status || x.status) === 'Completed').length;
            if (total > 0) map[id] = { total, done };
          }
        } catch { /* ignore */ }
      }));
      if (!cancelled) setCounts(map);
    })();
    return () => { cancelled = true; };
  }, [idKey, refreshKey]);
  return counts;
}
const TaskRow = ({ task, project, subtaskProgress, onOpen, onToggleComplete }) => {
  const pri = priorityMeta(task.priority);
  const overdue = isOverdue(task.date_due, task.status);
  const isDone = task.status === 'Completed';
  const openDetail = (e) => {
    // Ignore clicks that originated on the checkbox or an inline control.
    if (e.target.closest('input, button, select, a')) return;
    onOpen(task.id);
  };
  const onKeyOpen = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(task.id); }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      className={`tp-row ${isDone ? 'is-done' : ''}`}
      onClick={openDetail}
      onKeyDown={onKeyOpen}
    >
      <span className="tp-row-drag" aria-hidden="true">⋮⋮</span>
      <input
        type="checkbox"
        className="tp-row-check"
        checked={isDone}
        onChange={() => onToggleComplete(task)}
        aria-label={`Mark ${task.name} ${isDone ? 'incomplete' : 'complete'}`}
      />
      <div className="tp-row-name">
        <span className={isDone ? 'tp-name-done' : ''}>{task.name || '(untitled)'}</span>
      </div>
      <div className="tp-cell tp-cell-project">
        {project ? <span className="tp-chip">{project.name}</span> : <span className="tp-cell-empty">—</span>}
      </div>
      <div className="tp-cell tp-cell-assignee">
        {task.assigned_user_name ? (
          <span className="tp-assignee">
            <span className="tp-avatar" aria-hidden="true">{initialsOf(task.assigned_user_name)}</span>
            <span className="tp-assignee-name">{task.assigned_user_name.trim()}</span>
          </span>
        ) : <span className="tp-cell-empty">Unassigned</span>}
      </div>
      <div className="tp-cell tp-cell-priority">
        <span className="tp-priority" style={{ color: pri.color }}>
          <span aria-hidden="true">{pri.icon}</span> {pri.label}
        </span>
      </div>
      <div className={`tp-cell tp-cell-due ${overdue ? 'is-overdue' : ''}`}>
        <span aria-hidden="true" className="tp-cell-icon">🗓</span> {fmtDueDate(task.date_due)}
      </div>
      <div className="tp-cell tp-cell-subs">
        {subtaskProgress ? (
          <span className="tp-subtask-progress">
            <span className="tp-subtask-label">{subtaskProgress.done} of {subtaskProgress.total}</span>
            <span className="tp-progress-bar" aria-hidden="true">
              <span className="tp-progress-fill" style={{ width: `${Math.round((subtaskProgress.done / subtaskProgress.total) * 100)}%` }} />
            </span>
          </span>
        ) : <span className="tp-cell-empty">—</span>}
      </div>
      <div className="tp-cell tp-cell-comments" aria-hidden="true">
        <span className="tp-cell-icon">💬</span>
      </div>
    </div>
  );
};

// ------------------------ Detail view ------------------------

const STATUS_CHOICES = ['Not Started', 'In Progress', 'Pending Input', 'Blocked', 'Deferred', 'Completed'];
const PRIORITY_CHOICES = ['Urgent', 'High', 'Medium', 'Low'];

export const TaskDetailView = ({ task, allTasks, projectsByTaskId, onBack, onSelect, onPatch, onRefresh, currentUser, backLabel = '← Back to Tasks' }) => {
  const project = projectsByTaskId[task.id];
  const [description, setDescription] = useState(task.description || '');
  const [users, setUsers] = useState([]);

  useEffect(() => { setDescription(task.description || ''); }, [task.id, task.description]);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${CHAT_API_BASE}/coworkers.php`);
        const d = await r.json();
        if (d.success) setUsers(d.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const saveDescription = async () => {
    if (description === (task.description || '')) return;
    await onPatch(task.id, { description });
  };

  // Search + filter for the compact left-side list.
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const leftList = allTasks.filter(t => {
    if (statusFilter && t.status !== statusFilter) return false;
    if (q && !(t.name || '').toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="tp-page tp-detail">
      <div className="tp-detail-header">
        <button className="back-button" onClick={onBack}>{backLabel}</button>
      </div>

      <div className="tp-detail-body">
        {/* Left compact task list */}
        <aside className="tp-detail-list">
          <div className="tp-detail-list-title">
            <h2>Tasks</h2>
          </div>
          <div className="tp-detail-list-filters">
            <div className="tp-search tp-search-sm">
              <span className="tp-search-icon" aria-hidden="true">🔍</span>
              <input type="search" placeholder="Search tasks…" value={q} onChange={e => setQ(e.target.value)} aria-label="Search tasks" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} aria-label="Filter by status" className="tp-detail-filter">
              <option value="">Status</option>
              {STATUS_CHOICES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="tp-detail-list-columns">
            <span>Task</span>
            <span>Status</span>
          </div>
          <ul className="tp-detail-list-items">
            {leftList.map(t => {
              const m = statusMeta(t.status);
              return (
                <li key={t.id}>
                  <button
                    className={`tp-detail-list-row ${t.id === task.id ? 'active' : ''}`}
                    onClick={() => onSelect(t.id)}
                  >
                    <input type="checkbox" readOnly checked={t.status === 'Completed'} aria-label="Mark complete" onClick={e => e.stopPropagation()} />
                    <span className="tp-detail-list-body">
                      <span className="tp-detail-list-name">{t.name || '(untitled)'}</span>
                      <span className="tp-detail-list-sub">
                        {projectsByTaskId[t.id]?.name || 'No project'}
                      </span>
                    </span>
                    <span className="tp-status-pill" style={{ color: m.color, borderColor: m.color }}>{m.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="tp-detail-list-footer">Showing {leftList.length} of {allTasks.length} tasks</div>
        </aside>

        {/* Main detail column */}
        <section className="tp-detail-main">
          <div className="tp-detail-top">
            <div className="tp-detail-top-left">
              <input
                type="checkbox"
                checked={task.status === 'Completed'}
                onChange={() => onPatch(task.id, { status: task.status === 'Completed' ? 'Not Started' : 'Completed' })}
                aria-label="Mark complete"
              />
              <input
                key={task.id}
                className="tp-detail-name"
                defaultValue={task.name || ''}
                onBlur={e => { if (e.target.value !== task.name) onPatch(task.id, { name: e.target.value }); }}
                aria-label="Task name"
              />
              <span className="tp-detail-id">ID: {String(task.id || '').slice(0, 8)}…</span>
            </div>
            <div className="tp-detail-top-actions">
              <button className="tp-btn tp-btn-outline" onClick={onRefresh}>⟳ Refresh</button>
            </div>
          </div>

          {(project || task.room_name) && (
            <div className="tp-breadcrumb">
              {project?.name || 'No project'}
              {task.room_name && <> <span aria-hidden="true">/</span> {task.room_name}</>}
            </div>
          )}

          <div className="tp-detail-grid">
            <div className="tp-detail-col-main">
              <section className="tp-panel">
                <div className="tp-panel-head">
                  <h3>Description</h3>
                </div>
                <textarea
                  className="tp-textarea"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  onBlur={saveDescription}
                  placeholder="Add a description…"
                  rows={4}
                />
              </section>

              <section className="tp-panel tp-panel-subtasks">
                <TaskSubtasks
                  parentTask={task}
                  currentUser={currentUser}
                  taskUsers={users}
                  onOpenSubtask={(st) => onSelect(st.id, st)}
                  onChanged={onRefresh}
                />
              </section>

              <section className="tp-panel tp-panel-activity">
                <div className="tp-panel-head">
                  <h3>Activity</h3>
                </div>
                <TaskConversations
                  tasks={[{ id: task.id, attributes: task, responses: task.responses || [] }]}
                  currentUser={currentUser}
                  onTaskChanged={onRefresh}
                />
              </section>
            </div>

            <aside className="tp-detail-col-fields">
              <FieldRow label="Status">
                <select value={task.status || ''} onChange={e => onPatch(task.id, { status: e.target.value })}>
                  {STATUS_CHOICES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Assignee">
                {(() => {
                  // coworkers.php returns `user_id`; users.php returns `id` — tolerate both.
                  const uid = (u) => u.user_id || u.id;
                  const uname = (u) => u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.user_name || u.username || '';
                  const opts = users.filter(u => uid(u));
                  const currentInList = !task.assigned_user_id || opts.some(u => uid(u) === task.assigned_user_id);
                  return (
                    <select value={task.assigned_user_id || ''} onChange={e => {
                      const u = users.find(x => uid(x) === e.target.value);
                      onPatch(task.id, {
                        assigned_user_id: e.target.value,
                        assigned_user_name: u ? uname(u) : ''
                      });
                    }}>
                      <option value="">Unassigned</option>
                      {!currentInList && (
                        <option value={task.assigned_user_id}>{(task.assigned_user_name || '').trim() || 'Current assignee'}</option>
                      )}
                      {opts.map(u => <option key={uid(u)} value={uid(u)}>{uname(u)}</option>)}
                    </select>
                  );
                })()}
              </FieldRow>
              <FieldRow label="Priority">
                <select value={task.priority || ''} onChange={e => onPatch(task.id, { priority: e.target.value })}>
                  <option value="">—</option>
                  {PRIORITY_CHOICES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Start date">
                <input
                  type="date"
                  value={(task.date_start || '').slice(0, 10)}
                  onChange={e => onPatch(task.id, { date_start: e.target.value })}
                />
              </FieldRow>
              <FieldRow label="Due date">
                <input
                  type="date"
                  value={(task.date_due || '').slice(0, 10)}
                  onChange={e => onPatch(task.id, { date_due: e.target.value })}
                />
              </FieldRow>
              <FieldRow label="Project">
                <div className="tp-field-readonly">{project?.name || 'No project'}</div>
              </FieldRow>
              <FieldRow label="Attachments">
                <div className="tp-field-readonly">0 attachments</div>
              </FieldRow>
              <button className="tp-add-field-btn" title="Coming soon" disabled>+ Add field</button>
            </aside>
          </div>

          <div className="tp-detail-footer">✓ Changes saved automatically</div>
        </section>
      </div>
    </div>
  );
};

const FieldRow = ({ label, children }) => (
  <div className="tp-field-row">
    <label className="tp-field-label">{label}</label>
    <div className="tp-field-value">{children}</div>
  </div>
);

export default TasksPage;
